import { useState } from 'react';
import { validateCardNumber, validateConfirmCardNumber, validateOnlyNumbers } from '@/features/card/components/ActivateCardModal/activateCard.validators';
import { useActivateCard } from '@/features/onboarding/hooks/useActivateCard';
import { useSetPassword } from '@/features/onboarding/hooks/useSetPassword';
import { isAdmin, isBasic } from '@/shared/config/permissions';
import { t } from '@/i18n';

export const CARD_MAX_LENGTH = 19;
export const PASSWORD_MIN_LENGTH = 8;


export const useOnboardingStep = ({ user, onSuccess, token }: { user?: any, onSuccess: () => void, token?: string }) => {
    const [error, setError] = useState<string | null>(null);
    const [state, setState] = useState<any>({
        step: "",
        cardNumber: '',
        confirmCardNumber: '',
        password: '',
        confirmPassword: '',
        errors: {},
    });

    const { activateCard } = useActivateCard();
    const { setupPassword } = useSetPassword();

    /* ---------------- Setup Password ---------------- */

    const isLengthValid = state.password.length >= PASSWORD_MIN_LENGTH;
    const hasNumber = /\d/.test(state.password);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(state.password);
    const hasUppercase = /[A-Z]/.test(state.password);

    const isValidPassword = isLengthValid && hasNumber && hasSpecial && hasUppercase;
    const isMatchValid = state.password.length > 0 && state.password === state.confirmPassword;
    const isFormValid = isValidPassword && isMatchValid;

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setState((prev: any) => ({
            ...prev,
            password: value,
            errors: {
                ...prev.errors
            },
        }));
    };

    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setState((prev: any) => ({
            ...prev,
            confirmPassword: value,
            errors: {
                ...prev.errors
            },
        }));
    };

    const handleSetupPassword = async () => {
        if (!isFormValid) return;

        const payload = {
            token: token || "",
            new_password: state.password,
        }

        const success = await setupPassword(payload);
        if (success) {
            onSuccess();
        }
    };


    /* ---------------- Card Number ---------------- */

    const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = validateOnlyNumbers(e.target.value);
        const error = validateCardNumber(value);

        setState((prev: any) => {
            const confirmError = prev.confirmCardNumber
                ? validateConfirmCardNumber(prev.confirmCardNumber, value)
                : prev.errors.confirmCardNumber;

            return {
                ...prev,
                cardNumber: value,
                errors: {
                    ...prev.errors,
                    cardNumber: error,
                    confirmCardNumber: confirmError
                },
            };
        });
    };

    const handleConfirmCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = validateOnlyNumbers(e.target.value);
        const error = validateConfirmCardNumber(value, state.cardNumber);

        setState((prev: any) => ({
            ...prev,
            confirmCardNumber: value,
            errors: { ...prev.errors, confirmCardNumber: error },
        }));
    };

    /* ---------------- Activate Card ---------------- */

    const handleSubmitActivateCard = async () => {
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }

        const payload = {
            user_uuid: user.user_uuid,
            customer_uuid: user.customer_uuid,
            assigned_limit: isBasic(user.role) ? 0 : Number(state.spendingLimit) / 100,
            role: user.role as string,
            status: isAdmin(user.role) ? "enabled" : "pending_approval",
            user: {
                uuid: user.uuid,
                fullname: `${user.name ?? ''} ${user.lname ?? ''} ${user.slname ?? ''}`.trim(),
                email: user.email
            },
            card_number: state.cardNumber.replace(/\s/g, '')
        };

        const success = await activateCard(payload);
        if (success) {
            onSuccess();
        }
    };

    const validate = () => {
        if (!state.cardNumber) return t('onboarding.activateCard.errors.panRequired');
        return null;
    };

    return {
        state,
        CARD_MAX_LENGTH,
        error,
        isLengthValid,
        hasUppercase,
        hasNumber,
        hasSpecial,
        isFormValid,
        handleSetupPassword,
        handlePasswordChange,
        handleConfirmPasswordChange,
        handleCardNumberChange,
        handleConfirmCardNumberChange,
        handleSubmitActivateCard,
        setState,
    };
};
